/* ———————————————————— Copyright (c) 2022 Garrett Watson ————————————————————
 *
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms,
 * with or without modification, are permitted provided
 * that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright
 *   notice, this list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright
 *  notice, this list of conditions and the following disclaimer in the
 * documentation and/or other materials provided with the distribution.
 *
 * 3. Neither the name of the copyright holder nor the names of its
 *  contributors may be used to endorse or promote products derived
 * from this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 *
 * ——————————————————————————————————————————————————————————————————————————— */

import { format } from 'date-fns'
import jspreadsheet, { Worksheet } from 'jspreadsheet'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import '/node_modules/jspreadsheet/dist/jspreadsheet.css'
import '/node_modules/jsuites/dist/jsuites.css'

type Race = {
  race: string
  d: number | string
  r: number | string
  reporting: number | string
  updated: string
  total: number | string
}

type RacesData = {
  house: Race[]
  senate: Race[]
  governor: Race[]
}

// Helper function to fetch JSON from a url
const fetchJSON = async (url: string) => {
  const res = await fetch(url)
  return await res.json()
}

// JSpreadsheet sucks
const license =
  'MmIyOGNlYTBjMjQ3YjA1OWE0ZGY1OThiYTMxNDExOGQzMGMxODlhYmZmYWE1YzQyNzBkY2NlYTY2MmFiMDViZmJjZWY0ODExY2VlYTFkZmMyZWQyZTE4Y2M1ZmIwOTdkOGQ0MWE1NmU5NTg1YTM5YTQ1NThhMWE0MDcyZGI1NjIsZXlKdVlXMWxJam9pYVhOdklpd2laR0YwWlNJNk1UWTJPVGt6T1RJd01Dd2laRzl0WVdsdUlqcGJJbk5vWldWMGN5MWxlR0Z0Y0d4bExuQmhaMlZ6TG1SbGRpSXNJbXh2WTJGc2FHOXpkQ0pkTENKd2JHRnVJam93TENKelkyOXdaU0k2V3lKMk55SXNJblk0SWl3aWRqa2lYWDA9'

// Format race urls
const formatRaceURL = (race: string) =>
  `https://projects.fivethirtyeight.com/2022-election-forecast/${race}_search.json`

// Define race urls
const raceURLs = {
  house: formatRaceURL('house'),
  senate: formatRaceURL('senate'),
  governor: formatRaceURL('governor'),
}

// Get all races
const races = {
  house: (await fetchJSON(raceURLs.house)).map(
    (r: any[]) => `HG-${r[0]}-${r[1]}`
  ),
  senate: (await fetchJSON(raceURLs.senate)).map((r: any[]) => `SG-${r[0]}`),
  governor: (await fetchJSON(raceURLs.governor)).map(
    (r: any[]) => `GG-${r[0]}`
  ),
}

// Change the year in the url to get the latest data
const formatURL = (id: string) =>
  `https://politics-elex-results.data.api.cnn.io/results/view/2020-${id}.json`

// Make the race names pretty
const formatRace = (r: string) => {
  const x = r.split('-')
  return `${x.slice(1).join('-')} ${
    x[0] === 'GG' ? 'Gov' : x[0] === 'SG' ? 'Sen' : ''
  }`
}

// Grab the data from CNN's API and reassemble it into a format that
// jspreadsheet can use (an array of objects)
const mapRaces = async (r: string) => {
  const url = formatURL(r)
  const res = await fetchJSON(url)
  if (res === null) {
    return {
      race: formatRace(r),
      d: '',
      r: '',
      reporting: '',
      updated: '',
      total: '',
    }
  }
  const candidates = res.candidates.map((c: any) => ({
    name: c.fullName,
    party: c.candidatePartyCode,
    percent: c.votePercentStr,
  }))

  // Calculate the total percent of coverage
  const totalPercent = candidates.reduce(
    (acc: any, cur: any) => acc + Number(cur.percent),
    0
  )

  // Find the democratic candidate
  const dem = candidates.find((c: any) => c.party === 'D')

  // Find the republican candidate
  const rep = candidates.find((c: any) => c.party === 'R')

  // This time around we don't care if the field hasn't been updated
  // since the last time we checked, so we don't need to bother with
  // checking the timestamp against the last timestamp we saved
  const timestamp = res?.voteTimestamp && new Date(res.voteTimestamp)

  return {
    race: formatRace(r),
    d: dem?.percent,
    r: rep?.percent,
    reporting: totalPercent.toFixed(1),
    updated:
      timestamp && !isNaN(timestamp.getDay())
        ? format(timestamp, 'MM/dd/yyyy @ hh:mm a')
        : null,
    total: +(res?.totalVote || 0),
  }
}

// Configure columns
const columns = [
  {
    type: 'text',
    title: 'Race',
    name: 'race',
  },
  {
    type: 'number',
    title: 'D',
    name: 'd',
    width: 45,
  },
  {
    type: 'number',
    title: 'R',
    name: 'r',
    width: 45,
  },
  {
    type: 'number',
    title: 'Reporting',
    name: 'reporting',
    width: 100,
  },
  {
    type: 'text',
    title: 'Updated',
    name: 'updated',
    width: 200,
  },
  {
    type: 'number',
    title: 'Total Votes',
    name: 'total',
    width: 100,
  },
]

// Configure worksheets
const worksheets: Worksheet[] = [
  {
    worksheetName: '2022 House Results',
    minDimensions: [columns.length, races.house.length],
    columns,
  } as Worksheet,
  {
    worksheetName: '2022 Senate Results',
    minDimensions: [columns.length, races.senate.length],
    columns,
  } as Worksheet,
  {
    worksheetName: '2022 Governor Results',
    minDimensions: [columns.length, races.governor.length],
    columns,
  } as Worksheet,
]

function App() {
  const jssRef = useRef<any>(null)
  const [racesData, setRacesData] = useState<RacesData>({} as RacesData)
  const [selection, setSelection] = useState<string>('')

  useEffect(() => {
    // Create only once
    if (!jssRef.current!.jspreadsheet) {
      jspreadsheet(jssRef.current, {
        worksheets,
        contextMenu: () => false,
        oncopy: () => false,
        onselection(worksheet, px, py, ux, uy) {
          const data = worksheet.getData()
          const processedData =
            data instanceof Array && !(px === ux && py === uy)
              ? data.reduce(
                  (
                    acc: string,
                    current: {
                      [key: string]: string
                    },
                    idx
                  ) => {
                    if (py > idx || uy < idx) return acc
                    const selectedCurrent = Object.values(current).slice(
                      px,
                      ux + 1
                    )
                    return `${acc}${selectedCurrent
                      .map((e) => {
                        if (e === null) return ''
                        return e.toString().trim()
                      })
                      .join('\t')}\n`
                  },
                  ''
                )
              : Object.values(data[py])[px]
          setSelection(processedData)
        },
        license,
      })
    }
  }, [])

  useEffect(() => {
    // Update data
    const updateData = async () => {
      const house = await Promise.all(races.house.map(mapRaces))
      const senate = await Promise.all(races.senate.map(mapRaces))
      const governor = await Promise.all(races.governor.map(mapRaces))
      setRacesData({
        house,
        senate,
        governor,
      })
    }

    // Grab our initial data
    updateData()

    // Update every 30 seconds
    const interval = setInterval(updateData, 30000)
    return () => clearInterval(interval)
  }, [])

  // Update jspreadsheet
  useEffect(() => {
    const house = jssRef.current!.jspreadsheet[0]
    const senate = jssRef.current!.jspreadsheet[1]
    const governor = jssRef.current!.jspreadsheet[2]
    try {
      // Update house
      house.setData(racesData.house)
      // Update senate
      senate.setData(racesData.senate)
      // Update governor
      governor.setData(racesData.governor)
    } catch (e) {
      console.error(e)
    }
  }, [racesData])

  // Handle copy event because jspreadsheet is a bit buggy
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
        window.navigator.clipboard.writeText(selection)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selection])

  // Render
  return (
    <div>
      <h1>- Welcome to LTE Sheets</h1>
      <Sheet ref={jssRef} />
    </div>
  )
}

// Make the sheet pretty
const Sheet = styled.div`
  .jss > tbody > tr {
    & > td:nth-child(3) {
      background-color: rgba(0, 0, 255, 0.1);
      color: rgba(0, 0, 255, 0.8);
    }

    & > td:nth-child(4) {
      background-color: rgba(255, 0, 0, 0.1);
      color: rgba(255, 0, 0, 0.8);
    }
  }
`

export default App
